import type { ConfigParameters, ParameterValue } from './registry';

/** The `chrome.storage.local` key dev/QA overrides live under. Mirrored in `extension-chrome` `StoreKeys`. */
export const GATE_OVERRIDES_STORE_KEY = 'feature_gate_overrides';

/** Frontend -> extension page. Widens the existing `GET_DEBUG_INFO` round-trip's message set. */
export const SET_FEATURE_GATE_OVERRIDES_MESSAGE_TYPE = 'SET_FEATURE_GATE_OVERRIDES';

/**
 * Dev/QA-only values that win over the envelope/registry default. Every non-gate `Kind` overrides
 * through `configs`.
 */
export interface GateOverrides {
	readonly gates?: Readonly<Record<string, boolean>>;
	readonly configs?: Readonly<Record<string, ConfigParameters>>;
}

export type GateOverridesValidation =
	| { readonly ok: true; readonly overrides: GateOverrides }
	| { readonly ok: false };

/**
 * Deliberately permissive: an absent/malformed store falls back to no overrides rather than
 * blocking bootstrap.
 */
export function validateGateOverrides(value: unknown): GateOverridesValidation {
	if (value === undefined || value === null) {
		return { ok: true, overrides: {} };
	}
	if (!isRecord(value)) {
		return { ok: false };
	}
	if (value.gates !== undefined && !isBooleanMap(value.gates)) {
		return { ok: false };
	}
	if (value.configs !== undefined && !isConfigMap(value.configs)) {
		return { ok: false };
	}
	return { ok: true, overrides: value as GateOverrides };
}

/**
 * Drops the parameters {@link validateGateOverrides} would refuse, keeping the rest. For producers
 * merging into the live SDK's override store, whose leaves are `unknown` and which dev tooling also
 * writes: posting such a bag whole fails validation and loses every override in it, not just the
 * offending parameter. Kept next to the validator so the two cannot drift.
 */
export function toStorableParameters(bag: Readonly<Record<string, unknown>>): ConfigParameters {
	const storable: Record<string, ParameterValue> = {};
	for (const [parameter, value] of Object.entries(bag)) {
		if (isParameterValue(value)) {
			storable[parameter] = value;
		}
	}
	return storable;
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isBooleanMap(value: unknown): value is Record<string, boolean> {
	return isRecord(value) && Object.values(value).every((entry) => typeof entry === 'boolean');
}

function isConfigMap(value: unknown): value is Record<string, ConfigParameters> {
	return isRecord(value) && Object.values(value).every(isParameterBag);
}

/**
 * Checked to the leaves, not just to the bag: `applyGateOverrides` copies these into the frozen
 * snapshot, so a shallow check would break the readers' `ParameterValue` promise with no error.
 */
function isParameterBag(value: unknown): value is ConfigParameters {
	return isRecord(value) && Object.values(value).every(isParameterValue);
}

function isParameterValue(value: unknown): value is ParameterValue {
	if (Array.isArray(value)) {
		return value.every((entry) => typeof entry === 'string');
	}
	return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}
